// Unified vault search — mounted on the shared vault umbrella tab bar
// (partials/vault-tabs.html), present on both /vault/totp and
// /vault/credentials. Fetches both list APIs once per page life (cached),
// filters client-side, and navigates to /vault/{totp,credentials}?focus=<id>
// — the target page's own ?focus handling (see totp-vault.js / cred-vault.js)
// opens that item's detail on load and then strips the query param.
//
// No-ops on any page without #vaultUnifiedSearch (i.e. every non-vault page),
// so main.js can call initVaultSearch() unconditionally on every page load.
import { debounce } from './utils.js';

// Injected by partials/vault-tabs.html (window.VAULT_SEARCH_STR); the
// fallback only matters if the module is ever loaded without that partial
// having rendered first.
const STR = (typeof window !== 'undefined' && window.VAULT_SEARCH_STR) || { group_totp: 'TOTP', group_cred: 'Credentials' };

export function initVaultSearch() {
    const input = document.getElementById('vaultUnifiedSearch');
    if (!input) return;
    const dropdown = document.getElementById('vaultUnifiedResults');
    if (!dropdown) return;
    let cache = null;

    async function loadAll() {
        if (cache) return cache;
        const [totpRes, credRes] = await Promise.all([
            fetch('/api/vault/totp', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' }),
            fetch('/api/vault/credentials', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' }),
        ]);
        // Both list endpoints return a bare JSON array of items (see
        // listTOTPVaultItems in server/totp_vault.go and listCredVaultItems in
        // server/cred_vault.go) — no { items: [...] } wrapper.
        const totp = totpRes.ok ? (await totpRes.json()) : [];
        const cred = credRes.ok ? (await credRes.json()) : [];
        cache = { totp: totp || [], cred: cred || [] };
        return cache;
    }

    const run = debounce(async function () {
        const q = input.value.trim().toLowerCase();
        if (q.length < 2) { dropdown.hidden = true; return; }
        let data;
        try {
            data = await loadAll();
        } catch (_) {
            dropdown.hidden = true;
            return;
        }
        const tHits = data.totp.filter(function (it) {
            return ((it.issuer || '') + ' ' + (it.account_name || '')).toLowerCase().includes(q);
        }).slice(0, 8);
        const cHits = data.cred.filter(function (it) {
            return ((it.title || '') + ' ' + (it.tags || []).join(' ')).toLowerCase().includes(q);
        }).slice(0, 8);
        renderGroups(dropdown, tHits, cHits);
        dropdown.hidden = tHits.length + cHits.length === 0;
    }, 250);

    input.addEventListener('input', run);
    document.addEventListener('keydown', function (e) {
        if (e.key === '/' && document.activeElement !== input
            && !/INPUT|TEXTAREA|SELECT/.test(document.activeElement.tagName)) {
            e.preventDefault();
            input.focus();
        }
        if (e.key === 'Escape') { dropdown.hidden = true; }
    });
    document.addEventListener('click', function (e) {
        if (!dropdown.contains(e.target) && e.target !== input) dropdown.hidden = true;
    });
}

function renderGroups(dropdown, totpHits, credHits) {
    dropdown.innerHTML = '';
    appendGroup(dropdown, STR.group_totp, totpHits, function (it) {
        return { label: (it.issuer || '') + ' — ' + (it.account_name || ''), href: '/vault/totp?focus=' + encodeURIComponent(it.id) };
    });
    appendGroup(dropdown, STR.group_cred, credHits, function (it) {
        return { label: it.title, href: '/vault/credentials?focus=' + encodeURIComponent(it.id) };
    });
}

function appendGroup(dropdown, title, hits, toRow) {
    if (!hits.length) return;
    const h = document.createElement('div');
    h.className = 'vault-search-group';
    h.textContent = title;
    dropdown.appendChild(h);
    hits.forEach(function (it) {
        const row = toRow(it);
        const a = document.createElement('a');
        a.className = 'vault-search-row';
        a.href = row.href;
        a.textContent = row.label;
        dropdown.appendChild(a);
    });
}
